import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';

import { RoomTile } from 'Components/RocketScan';
import { useOnScreen } from 'Hooks/useOnScreen';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { projectSelector } from 'Containers/RocketScan/selectors';
import { setLocation } from 'Containers/RocketScan/MultiUnit/Locations/actions';
import { setSelectedRoomId } from 'Containers/RocketScan/RoomsView/RoomButtons/actions';
import { listRoomImageProcessors } from 'Containers/RocketScan/MultiUnit/RoomTiles/RoomTile/actions';
import { setRoomObject } from 'Containers/RocketScan/RoomsView/Rooms/actions';
import { checkIfPhotoSelected } from 'Utils/helpers';
import {
  selectAllModeSelector,
  selectedRoomsSelector,
  selectPhotosModeSelector,
  unSelectedRoomsSelector,
} from 'Containers/RocketScan/Header/ActionsCenter/selectors';
import { setSelectedRooms, setUnSelectedRooms } from 'Containers/RocketScan/Header/ActionsCenter/actions';
import { pusherSelector } from 'Containers/Core/selectors';
import { roomScopeTotalSelector } from 'Containers/RocketScan/RoomsView/DamagedMaterials/selectors';
import { getRoomScopeTotal } from 'Containers/RocketScan/RoomsView/DamagedMaterials/actions';
import { userFeatureFlagsSelector } from 'Containers/User/selector';

interface Props {
  location: any;
  roomId: number;
  roomName: string;
  locationId?: number;
  locationName?: string;
  photosCount: number;
  thumbnailSrcUrl?: string;
  isLastItem: boolean;
  currentPage: number;
  lastPage: number;
  roomsData: any;
  photos: any[];
  getRooms: (pageNumber: number) => void;
}

const RoomTileContainer = ({
  location,
  roomId,
  roomName,
  locationId,
  locationName,
  photosCount,
  thumbnailSrcUrl,
  isLastItem,
  currentPage,
  lastPage,
  roomsData,
  photos,
  getRooms,
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const ref = useRef(null);
  const mounted = useRef(false);

  const isOnScreen = useOnScreen(ref);

  const { imageProcessor } = useSelector(userFeatureFlagsSelector, areEqual);
  const project = useSelector(projectSelector, areEqual);
  const pusher = useSelector(pusherSelector, areEqual);
  const selectPhotosMode = useSelector(selectPhotosModeSelector, areEqual);
  const selectAllMode = useSelector(selectAllModeSelector, areEqual);
  const selectedRooms = useSelector(selectedRoomsSelector, areEqual);
  const unSelectedRooms = useSelector(unSelectedRoomsSelector, areEqual);

  const scopeTotal = useSelector((state) => roomScopeTotalSelector(state, roomId), areEqual);

  const [isLoading, setIsLoading] = useState(false);
  const [imageProcessorChannels, setImageProcessorChannels] = useState<string[]>([]);

  const onClick = useCallback(() => {
    if (selectPhotosMode && !selectAllMode && photos.length > 0) {
      dispatch(
        setSelectedRooms({
          id: roomId,
          photos,
          photos_count: photosCount,
        })
      );
    } else if (selectAllMode && photos.length > 0) {
      dispatch(
        setUnSelectedRooms({
          id: roomId,
          photos,
          photos_count: photosCount,
        })
      );
    } else {
      const { id: locationId } = location;
      const { id: projectId } = project;

      dispatch(setRoomObject(roomsData));
      dispatch(setLocation(location));
      dispatch(setSelectedRoomId(roomId));
      history.push(`/projects/${projectId}/rocketscan/multiunit/${locationId}`);
    }
  }, [roomsData, selectPhotosMode, selectAllMode, roomId, photos, photosCount]);

  const checkImageProcessorPending = useCallback(async () => {
    if (!imageProcessor) return;

    const response: any = await dispatch(listRoomImageProcessors(roomId));

    if (response?.data && mounted.current) {
      const { data } = response;
      const processingAssemblies = data.filter((processor: any) =>
        processor.status === 'processing' || processor.status === 'pending'
      );

      if (processingAssemblies.length > 0) {
        setIsLoading(true);

        const channels = processingAssemblies.map((processor: any) =>
          `imageprocessornotification.AssemblyId.${processor.assembly_id}`
        );
        setImageProcessorChannels(channels);
      } else {
        // No active assemblies; clear loading and subscriptions.
        setIsLoading(false);
        if (imageProcessorChannels.length > 0) {
          setImageProcessorChannels([]);
        }
      }
    }
  }, [roomId, mounted, imageProcessor, dispatch, imageProcessorChannels]);

  useEffect(() => {
    mounted.current = true;
    if (mounted.current) {
      (async () => {
        await checkImageProcessorPending();
      })();
    }

    return () => {
      mounted.current = false;
    };
  }, [checkImageProcessorPending]);

  useEffect(() => {
    dispatch(getRoomScopeTotal(roomId));
  }, [roomId]);

  // Subscribe to ImageProcessor Pusher events
  useEffect(() => {
    if (!imageProcessor || imageProcessorChannels.length === 0) {
      return;
    }

    const subscriptions = imageProcessorChannels.map((channelName) => {
      const subscription = pusher.subscribe(channelName);
      subscription.bind('ImageProcessorUpdated', () => {
        checkImageProcessorPending();
      });
      subscription.bind('ImageProcessorPhotoUpdated', () => {
        // Optional: Handle individual file progress updates
      });
      return { channelName, subscription };
    });

    return () => {
      subscriptions.forEach(({ channelName, subscription }) => {
        subscription.unbind('ImageProcessorUpdated');
        subscription.unbind('ImageProcessorPhotoUpdated');
        subscription.unsubscribe(channelName);
      });
    };
  }, [imageProcessor, imageProcessorChannels, checkImageProcessorPending, pusher]);

  // lazy load next room tiles
  useEffect(() => {
    if (isOnScreen && isLastItem) {
      if (currentPage < lastPage) {
        (async function fetchData() {
          await getRooms(currentPage + 1);
        })();
      }
    }
  }, [isOnScreen]);

  useLayoutEffect(() => {
    if (selectAllMode) {
      dispatch(
        setSelectedRooms({
          id: roomId,
          photos,
          photosCount,
        })
      );
    } else {
      dispatch(setSelectedRooms({}));
    }
  }, [selectAllMode]);

  return (
    <RoomTile
      ref={ref}
      roomId={roomId}
      roomName={roomName}
      locationId={locationId}
      locationName={locationName}
      photosCount={photosCount}
      thumbnailSrcUrl={thumbnailSrcUrl}
      onClick={onClick}
      hasPendingAssemblies={isLoading}
      scopeTotal={scopeTotal}
      isRoomSelected={checkIfPhotoSelected(
        { id: roomId },
        selectedRooms,
        unSelectedRooms,
        selectPhotosMode,
        selectAllMode
      )}
    />
  );
};

RoomTileContainer.defaultProps = {
  thumbnailSrcUrl: undefined,
};

const RoomTileContainerMemo = memo(RoomTileContainer, areEqual);

export { RoomTileContainerMemo as RoomTile };
